import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Award,
  TrendingUp,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Heart,
  MessageCircle,
  Rocket
} from 'lucide-react';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  company: string;
  avatar: string;
  rating: number;
  content: string;
  date: string;
  location: string;
  industry: string;
  verified: boolean;
  featured: boolean;
  videoUrl?: string;
  audioUrl?: string;
  tags: string[];
  metrics: {
    transactionsProcessed: number;
    moneySaved: number;
    timeSaved: number;
    satisfaction: number;
  };
  social: {
    linkedin?: string;
    twitter?: string;
    website?: string;
  };
}

interface TestimonialsSectionProps {
  onViewAll?: () => void;
  onShare?: (testimonial: Testimonial) => void;
  onContact?: (testimonial: Testimonial) => void;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({
  onViewAll,
  onShare,
  onContact
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<Testimonial | null>(null);

  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Chen',
      role: 'CFO',
      company: 'TechFlow Solutions',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'NeoBridge has revolutionized our international payment processes. We\'ve reduced transaction costs by 60% and processing time by 80%. The platform\'s security features give us complete confidence in handling large-scale transactions.',
      date: '2024-01-15',
      location: 'San Francisco, CA',
      industry: 'Technology',
      verified: true,
      featured: true,
      tags: ['Cost Reduction', 'Security', 'Efficiency'],
      metrics: {
        transactionsProcessed: 50000,
        moneySaved: 250000,
        timeSaved: 1200,
        satisfaction: 98
      },
      social: {
        linkedin: 'https://linkedin.com/in/sarahchen',
        website: 'https://techflow.com'
      }
    },
    {
      id: '2',
      name: 'Marcus Rodriguez',
      role: 'Head of Operations',
      company: 'Global Manufacturing Inc.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'The compliance automation features are outstanding. We can now process payments to 40+ countries without worrying about regulatory issues. NeoBridge\'s AI-powered fraud detection has saved us from potential losses.',
      date: '2024-01-10',
      location: 'Chicago, IL',
      industry: 'Manufacturing',
      verified: true,
      featured: true,
      tags: ['Compliance', 'AI Detection', 'Global Reach'],
      metrics: {
        transactionsProcessed: 75000,
        moneySaved: 180000,
        timeSaved: 2000,
        satisfaction: 96
      },
      social: {
        linkedin: 'https://linkedin.com/in/marcusrodriguez'
      }
    },
    {
      id: '3',
      name: 'Dr. Aisha Patel',
      role: 'Finance Director',
      company: 'MediCare Global',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'As a healthcare organization, we need the highest security standards. NeoBridge\'s blockchain technology and HSM integration provide the security we require while maintaining excellent user experience.',
      date: '2024-01-08',
      location: 'Boston, MA',
      industry: 'Healthcare',
      verified: true,
      featured: true,
      tags: ['Healthcare', 'Security', 'Blockchain'],
      metrics: {
        transactionsProcessed: 30000,
        moneySaved: 150000,
        timeSaved: 800,
        satisfaction: 99
      },
      social: {
        linkedin: 'https://linkedin.com/in/aishapatel',
        website: 'https://medicareglobal.com'
      }
    },
    {
      id: '4',
      name: 'James Thompson',
      role: 'CEO',
      company: 'FinTech Innovations',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'The API integration was seamless. We were able to integrate NeoBridge into our existing systems within days. The real-time analytics and reporting features have given us unprecedented visibility into our payment flows.',
      date: '2024-01-05',
      location: 'Austin, TX',
      industry: 'FinTech',
      verified: true,
      featured: false,
      tags: ['API Integration', 'Analytics', 'Real-time'],
      metrics: {
        transactionsProcessed: 100000,
        moneySaved: 300000,
        timeSaved: 1500,
        satisfaction: 97
      },
      social: {
        linkedin: 'https://linkedin.com/in/jamesthompson',
        twitter: 'https://twitter.com/jamesthompson'
      }
    },
    {
      id: '5',
      name: 'Elena Volkov',
      role: 'Treasury Manager',
      company: 'EcoEnergy Corp',
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'NeoBridge\'s multi-currency support and real-time exchange rates have optimized our treasury operations. We\'ve seen a 40% improvement in our currency hedging strategies.',
      date: '2024-01-03',
      location: 'Seattle, WA',
      industry: 'Energy',
      verified: true,
      featured: false,
      tags: ['Multi-Currency', 'Treasury', 'Optimization'],
      metrics: {
        transactionsProcessed: 60000,
        moneySaved: 200000,
        timeSaved: 1000,
        satisfaction: 95
      },
      social: {
        linkedin: 'https://linkedin.com/in/elenavolkov'
      }
    },
    {
      id: '6',
      name: 'David Kim',
      role: 'VP of Finance',
      company: 'RetailMax International',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      rating: 5,
      content: 'The workflow automation features have streamlined our approval processes. What used to take days now takes hours. The platform\'s scalability has supported our rapid international expansion.',
      date: '2024-01-01',
      location: 'New York, NY',
      industry: 'Retail',
      verified: true,
      featured: false,
      tags: ['Automation', 'Scalability', 'Expansion'],
      metrics: {
        transactionsProcessed: 200000,
        moneySaved: 500000,
        timeSaved: 3000,
        satisfaction: 98
      },
      social: {
        linkedin: 'https://linkedin.com/in/davidkim',
        website: 'https://retailmax.com'
      }
    }
  ];

  const featuredTestimonials = testimonials.filter(t => t.featured);
  const allTestimonials = testimonials;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, featuredTestimonials.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleTestimonialClick = (testimonial: Testimonial) => {
    setSelectedTestimonial(testimonial);
  };

  const handleShare = (testimonial: Testimonial) => {
    onShare?.(testimonial);
  };

  const handleContact = (testimonial: Testimonial) => {
    onContact?.(testimonial);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="mb-4 bg-blue-100 text-blue-800 border-blue-200">
              <Heart className="h-4 w-4 mr-2" />
              Customer Success Stories
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted by Industry Leaders
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> Worldwide</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how businesses across industries are transforming their payment operations 
              with NeoBridge's innovative platform.
            </p>
          </div>

          {/* Stats Overview */}
          <div className={`grid gap-6 md:grid-cols-4 mb-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Card className="text-center p-6">
              <CardContent className="p-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">500+</div>
                <div className="text-sm text-gray-600">Happy Customers</div>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="p-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">$2.5B+</div>
                <div className="text-sm text-gray-600">Transactions Processed</div>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="p-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
                  <Globe className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">150+</div>
                <div className="text-sm text-gray-600">Countries Served</div>
              </CardContent>
            </Card>

            <Card className="text-center p-6">
              <CardContent className="p-0">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-4">
                  <Award className="h-6 w-6 text-orange-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">98%</div>
                <div className="text-sm text-gray-600">Customer Satisfaction</div>
              </CardContent>
            </Card>
          </div>

          {/* Featured Testimonials Carousel */}
          <div className={`mb-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Featured Success Stories</h3>
              <p className="text-gray-600">Real results from our customers</p>
            </div>

            <div className="relative">
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative h-96 bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="relative h-full flex items-center justify-center p-8">
                      <div className="text-center text-white max-w-4xl">
                        <Quote className="h-12 w-12 mx-auto mb-6 opacity-80" />
                        <blockquote className="text-xl md:text-2xl font-medium mb-8 leading-relaxed">
                          "{featuredTestimonials[currentIndex]?.content}"
                        </blockquote>
                        <div className="flex items-center justify-center space-x-4">
                          <Avatar className="h-16 w-16 border-4 border-white">
                            <AvatarImage src={featuredTestimonials[currentIndex]?.avatar} />
                            <AvatarFallback>
                              {featuredTestimonials[currentIndex]?.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="text-left">
                            <div className="font-semibold text-lg">
                              {featuredTestimonials[currentIndex]?.name}
                            </div>
                            <div className="text-blue-100">
                              {featuredTestimonials[currentIndex]?.role}, {featuredTestimonials[currentIndex]?.company}
                            </div>
                            <div className="flex items-center space-x-2 mt-2">
                              {renderStars(featuredTestimonials[currentIndex]?.rating || 0)}
                              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                                Verified
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Carousel Controls */}
              <div className="flex items-center justify-center space-x-4 mt-6">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePrevious}
                  className="h-10 w-10"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handlePlayPause}
                  className="h-10 w-10"
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleNext}
                  className="h-10 w-10"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Dots Indicator */}
              <div className="flex justify-center space-x-2 mt-4">
                {featuredTestimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 w-2 rounded-full transition-all ${
                      index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* All Testimonials Grid */}
          <div className={`mb-16 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">What Our Customers Say</h3>
              <p className="text-gray-600">Hear from businesses across different industries</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {allTestimonials.map((testimonial) => (
                <Card 
                  key={testimonial.id}
                  className="group cursor-pointer hover:shadow-lg transition-all duration-300"
                  onClick={() => handleTestimonialClick(testimonial)}
                >
                  <CardHeader>
                    <div className="flex items-start space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={testimonial.avatar} />
                        <AvatarFallback>
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                        <CardDescription className="text-sm">
                          {testimonial.role} at {testimonial.company}
                        </CardDescription>
                        <div className="flex items-center space-x-2 mt-2">
                          {renderStars(testimonial.rating)}
                          {testimonial.verified && (
                            <Badge variant="secondary" className="text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <blockquote className="text-gray-600 mb-4 line-clamp-3">
                      "{testimonial.content}"
                    </blockquote>
                    
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {testimonial.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="text-xs text-gray-500">
                        {testimonial.industry} • {testimonial.location}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className={`text-center transition-all duration-1000 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0">
              <CardContent className="p-12">
                <h3 className="text-3xl font-bold mb-4">Join Our Success Stories</h3>
                <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                  Ready to transform your payment operations? Join hundreds of businesses 
                  already experiencing the benefits of NeoBridge.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <Button 
                    size="lg" 
                    className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
                    onClick={onViewAll}
                  >
                    <Rocket className="h-5 w-5 mr-2" />
                    Start Your Success Story
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Schedule a Demo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSection;
